import AuthenticationContext, { TokenCallback } from 'adal-angular';

import { requiresInteractiveLogin } from '../adal';
import { AcquireTokenAuthError, InteractiveLoginRequiredAuthError, LoginInProgressAuthError } from '../errors';
import { AuthenticatorOptions, AuthenticatorType, PopupAuthenticator } from '../types';

export const loadFrameTimeout = 6000;
export const LoginInProgressAuthErrorMessage = 'Not yet authenticated. Redirecting to interactive login flow.';

const rejectAfter = (ms: number, error: Error) =>
  new Promise<never>((_resolve, reject) =>
    setTimeout(() => {
      reject(error);
    }, ms)
  );

export const handleCallback: TokenCallback = (_errorMessage, token) => {
  if (token) {
    location.reload();
  }
};

export default class AdalPopupAuthenticator implements PopupAuthenticator {
  public readonly type = AuthenticatorType.AdalPopup;
  private readonly context: AuthenticationContext;
  private readonly onError: AuthenticatorOptions['onError'];

  constructor(options: AuthenticatorOptions) {
    const { instance, tenant, clientId, redirectUri, extraQueryParameters, onError, postLogoutRedirectUri } = options;
    this.context = new AuthenticationContext({
      instance,
      tenant,
      clientId,
      redirectUri,
      extraQueryParameter: extraQueryParameters,
      cacheLocation: 'localStorage',
      navigateToLoginRequestUrl: true,
      loadFrameTimeout,
      popUp: true,
      callback: handleCallback,
      postLogoutRedirectUri,
    });
    this.onError = onError;
  }

  public async acquireToken([resource]: string[]): Promise<string> {
    try {
      if (this.hasTokenInUrl()) {
        this.handleTokenInUrl();
      }
      this.ensureUserCached();

      return await this.acquireTokenSilent(resource);
    } catch (firstError) {
      if (this.requiresLogin(firstError.errorCode)) {
        throw new LoginInProgressAuthError(LoginInProgressAuthErrorMessage);
      }

      await this.reportError(resource, new AcquireTokenAuthError({ ...firstError, attempts: 1 }));

      if (requiresInteractiveLogin(firstError)) {
        throw new InteractiveLoginRequiredAuthError(firstError.errorMessage);
      }

      try {
        return await this.acquireTokenSilent(resource);
      } catch (secondError) {
        if (this.requiresLogin(secondError.errorCode)) {
          throw new LoginInProgressAuthError(LoginInProgressAuthErrorMessage);
        }

        await this.reportError(resource, new AcquireTokenAuthError({ ...secondError, attempts: 2 }));

        if (requiresInteractiveLogin(secondError)) {
          throw new InteractiveLoginRequiredAuthError(secondError.errorMessage);
        }
        throw secondError;
      }
    }
  }

  public handleTokenInUrl() {
    this.context.handleWindowCallback(window.location.hash);
  }

  public loginPopup() {
    this.context.login();
  }

  public logout() {
    this.context.logOut();
  }

  public acquireTokenRedirect() {
    throw new Error('acquireTokenRedirect is not implemented');
  }

  public setThirdPartyCookieErrorCallback() {
    throw new Error('setThirdPartyCookieErrorCallback is not implemented');
  }

  public async acquireTokenPopup([resource]: string[]): Promise<void> {
    try {
      await new Promise((resolve, reject) => {
        const extraQueryParameter = undefined;
        const claims = undefined;
        const callback: TokenCallback = (errorMessage, token, errorCode) => {
          if (errorCode) {
            reject(new AcquireTokenAuthError({ errorCode, errorMessage: errorMessage ?? undefined }));
          }
          resolve(token);
        };

        this.context.acquireTokenPopup(resource, extraQueryParameter, claims, callback);
      });
      location.reload();
    } catch (error) {
      this.reportError(resource, error);
    }
  }

  private requiresLogin(errorCode: string) {
    return !this.ensureUserCached() || errorCode === 'login_required';
  }

  private ensureUserCached() {
    return this.context.getCachedUser();
  }

  private hasTokenInUrl() {
    return this.context.isCallback(window.location.hash);
  }

  private acquireTokenSilent(resource: string): Promise<string> {
    const acquireTokenPromise = new Promise<string>((resolve, reject) => {
      this.context.acquireToken(resource, (errorMessage, token, errorCode) => {
        if (errorCode) {
          reject(new AcquireTokenAuthError({ errorCode, errorMessage: errorMessage ?? undefined }));

          return;
        }
        resolve(token);
      });
    });
    const timeoutPromise = rejectAfter(loadFrameTimeout * 2, new AcquireTokenAuthError({ errorCode: 'adal_stuck' }));

    return Promise.race([acquireTokenPromise, timeoutPromise]);
  }

  private async reportError(resource: string, error: AcquireTokenAuthError) {
    // istanbul ignore else
    if (this.onError) {
      try {
        await this.onError(error, { authenticatorType: this.type, scopes: [resource] });
      } catch (err) {
        console.error(err);
      }
    }
  }
}
